import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Box, ChevronRight, MapPin, Move, QrCode } from "lucide-react";

import {
  useContainerContents,
  useContainers,
  useInventory,
  useLocationTree,
  useMoveContainer,
} from "@/api/queries";
import type { LocationNode } from "@/api/types";
import { useCanWrite } from "@/auth/useAuth";
import { Button } from "@/components/Button";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { Dialog } from "@/components/Dialog";
import { Field, Select } from "@/components/Input";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Photo } from "@/components/Photo";
import { useToast } from "@/components/toast-context";
import { LabelDialog } from "@/features/labels/LabelDialog";
import { PageHeader } from "@/layout/PageHeader";
import { FillCard, Page } from "@/layout/Page";
import { formatContainerType, formatQuantity, formatUnit } from "@/lib/format";

/** The container list (TICKET FE-009). */
export function ContainersPage() {
  const { data, isLoading, error, refetch } = useContainers({});

  return (
    <Page>
      <PageHeader
        title="Contenants"
        subtitle="Packouts, bacs, coffres. Déplacer un contenant déplace tout ce qu'il contient."
      />
      <FillCard>
        {isLoading && <LoadingState />}
        {error && <ErrorState error={error} onRetry={() => void refetch()} />}
        {data && data.length === 0 && (
          <EmptyState
            icon={<Box className="size-6" aria-hidden />}
            title="Aucun contenant"
            description="Un contenant vit dans un emplacement ou dans un autre contenant, jamais les deux."
          />
        )}
        {data && data.length > 0 && (
          <ul className="divide-y divide-border/60">
            {data.map((container) => (
              <li key={container.id}>
                <Link
                  to={`/containers/${container.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-surface-hover"
                >
                  <span className="flex size-10 items-center justify-center rounded-md border border-border bg-surface-sunken text-ink-subtle">
                    <Box className="size-5" aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] text-ink">
                      {container.name ?? container.container_code}
                    </span>
                    <span className="block truncate text-[12px] text-ink-subtle">
                      {container.container_code} · {formatContainerType(container.container_type)}
                    </span>
                  </span>
                  <ChevronRight className="size-4 shrink-0 text-ink-subtle" aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </FillCard>
    </Page>
  );
}

/** The container sheet: what is inside, and where it really is. */
export function ContainerDetailPage() {
  const { containerId = "" } = useParams();
  const [moving, setMoving] = useState(false);
  const [labelOpen, setLabelOpen] = useState(false);
  const canWrite = useCanWrite();

  const contents = useContainerContents(containerId, { enabled: Boolean(containerId) });

  if (contents.isLoading) return <LoadingState label="Chargement du contenant…" />;
  if (contents.error)
    return <ErrorState error={contents.error} onRetry={() => void contents.refetch()} />;
  if (!contents.data) return null;

  const { container, resolved_location, resolved_path, child_containers } = contents.data;

  return (
    <Page>
      <PageHeader
        crumbs={[{ label: "Contenants", to: "/containers" }, { label: container.container_code }]}
        title={container.name ?? container.container_code}
        subtitle={formatContainerType(container.container_type)}
        actions={
          <>
            <Button variant="secondary" onClick={() => setLabelOpen(true)}>
              <QrCode className="size-4" aria-hidden />
              Étiquette
            </Button>
            {canWrite && (
              <Button variant="primary" onClick={() => setMoving(true)}>
                <Move className="size-4" aria-hidden />
                Déplacer
              </Button>
            )}
          </>
        }
      />

      <div className="grid min-w-0 gap-3 @4xl:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-3">
        <Card>
          <CardHeader title="Où il se trouve" />
          <CardBody>
            {resolved_location ? (
              <>
                <nav aria-label="Chemin" className="flex flex-wrap items-center gap-1 text-[13px]">
                  {resolved_path.map((step, index) => (
                    <span key={step.id} className="flex items-center gap-1">
                      {index > 0 && <ChevronRight className="size-3 text-ink-subtle" aria-hidden />}
                      <Link to={`/locations/${step.id}`} className="text-ink-muted hover:text-brand">
                        {step.name}
                      </Link>
                    </span>
                  ))}
                </nav>
                <p className="mt-2 flex items-center gap-1.5 text-[12px] text-ink-subtle">
                  <MapPin className="size-3.5" aria-hidden />
                  Emplacement résolu à travers les contenants parents — il n'est pas stocké sur ce
                  contenant.
                </p>
              </>
            ) : (
              <p className="text-[13px] text-ink-subtle">Emplacement inconnu.</p>
            )}
            {container.parent_container_id && (
              <p className="mt-2 text-[13px] text-ink-muted">
                Rangé dans{" "}
                <Link
                  to={`/containers/${container.parent_container_id}`}
                  className="text-brand hover:underline"
                >
                  un autre contenant
                </Link>
                .
              </p>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Sous-contenants" />
          {child_containers.length === 0 ? (
            <EmptyState title="Aucun sous-contenant" description="Ce contenant n'en contient pas d'autre." />
          ) : (
            <ul className="divide-y divide-border/60">
              {child_containers.map((child) => (
                <li key={child.id}>
                  <Link
                    to={`/containers/${child.id}`}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface-hover"
                  >
                    <Box className="size-4 text-ink-subtle" aria-hidden />
                    <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
                      {child.name ?? child.container_code}
                    </span>
                    <span className="font-mono text-[11px] text-ink-subtle">
                      {child.container_code}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        </div>

        <div className="flex min-w-0 flex-col">
          <ContainerStockList containerId={containerId} />
        </div>
      </div>

      <LabelDialog
        open={labelOpen}
        onOpenChange={setLabelOpen}
        entity="container"
        entityId={containerId}
      />

      <MoveContainerDialog
        open={moving}
        onOpenChange={setMoving}
        containerId={containerId}
        currentLocationId={container.current_location_id}
      />
    </Page>
  );
}

function ContainerStockList({ containerId }: { containerId: string }) {
  const { data, isLoading, error, refetch } = useInventory({
    container_id: containerId,
    in_stock_only: true,
    limit: 100,
  });

  return (
    <Card>
      <CardHeader
        title="Stock dans ce contenant"
        subtitle="Les quantit\u00e9s sont celles de ce contenant, pas le total de l'entreprise."
      />
      {isLoading && <LoadingState />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      {data && data.items.length === 0 && (
        <EmptyState title="Vide" description="Aucun article n'est enregistr\u00e9 dans ce contenant." />
      )}
      {data && data.items.length > 0 && (
        <ul className="divide-y divide-border/60">
          {data.items.map((row) => (
            <li key={row.product_id}>
              <Link
                to={`/products/${row.product_id}`}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface-hover"
              >
                <Photo mediaId={row.media_id} alt={row.name} className="size-10 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] text-ink">{row.name}</span>
                  <span className="block truncate text-[11px] text-ink-subtle">
                    {[row.manufacturer, row.part_number].filter(Boolean).join(" \u00b7 ")}
                  </span>
                </span>
                <span className="shrink-0 text-right">
                  <span className="tnum block text-[15px] font-semibold text-ink">
                    {formatQuantity(row.available_quantity)}
                  </span>
                  <span className="block text-[10px] text-ink-subtle">
                    {formatUnit(row.unit)} dispo.
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function MoveContainerDialog({
  open,
  onOpenChange,
  containerId,
  currentLocationId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  containerId: string;
  currentLocationId: string | null;
}) {
  const toast = useToast();
  const { data: tree } = useLocationTree();
  const [locationId, setLocationId] = useState(currentLocationId ?? "");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMoveContainer({
    onSuccess: () => {
      toast.success("Contenant déplacé", "Tout son contenu a suivi.");
      onOpenChange(false);
    },
    onError: (cause) => setError(cause.message),
  });

  const flat = flattenTree(tree ?? []);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setError(null);
        onOpenChange(next);
      }}
      title="Déplacer le contenant"
      description="Tout ce qu'il contient suit automatiquement — aucun article n'est réécrit."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={!locationId}
            onClick={() => {
              setError(null);
              mutation.mutate({ containerId, location_id: locationId });
            }}
          >
            Déplacer
          </Button>
        </>
      }
    >
      <Field label="Nouvel emplacement" htmlFor="move-location">
        <Select
          id="move-location"
          value={locationId}
          onChange={(event) => setLocationId(event.target.value)}
        >
          <option value="">— Choisir —</option>
          {flat.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {"— ".repeat(entry.depth)}
              {entry.name} ({entry.code})
            </option>
          ))}
        </Select>
      </Field>
      {error && (
        <p role="alert" className="mt-3 rounded-md bg-danger-subtle px-3 py-2 text-[13px] text-danger">
          {error}
        </p>
      )}
    </Dialog>
  );
}

function flattenTree(
  nodes: LocationNode[],
  depth = 0,
): { id: string; code: string; name: string; depth: number }[] {
  return nodes.flatMap((node) => [
    { id: node.id, code: node.code, name: node.name, depth },
    ...flattenTree(node.children, depth + 1),
  ]);
}
